import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface CartItem {
  datastreamId: string;
  datastreamName: string;
  sourceId: string;
  sourceName: string;
  sourceDescription: string | null;
  subscriptionType: string | null;
  primaryUseCase: string | null;
  updateFrequency: string | null;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (datastreamId: string) => void;
  clearCart: () => void;
  getItemCount: () => number;
  isInCart: (datastreamId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Load cart from database when user logs in
  useEffect(() => {
    if (user) {
      loadCartFromDatabase();
    } else {
      // Clear cart when not logged in
      setItems([]);
      setLoading(false);
    }
  }, [user]);

  const loadCartFromDatabase = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('cart_items')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      const cartItems: CartItem[] = (data || []).map(item => ({
        datastreamId: item.datastream_id,
        datastreamName: item.datastream_name,
        sourceId: item.source_id,
        sourceName: item.source_name,
        sourceDescription: item.source_description,
        subscriptionType: item.subscription_type,
        primaryUseCase: item.primary_use_case,
        updateFrequency: item.update_frequency,
      }));

      setItems(cartItems);
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (item: CartItem) => {
    if (!user) {
      toast.error('U moet ingelogd zijn om items toe te voegen');
      return;
    }

    // Check if item already exists in local state
    const existing = items.find(i => i.datastreamId === item.datastreamId);
    if (existing) {
      toast.info('Dit item zit al in uw winkelmandje');
      return;
    }

    // Optimistically update UI
    setItems(prev => [...prev, item]);

    try {
      const { error } = await supabase
        .from('cart_items')
        .insert({
          user_id: user.id,
          datastream_id: item.datastreamId,
          datastream_name: item.datastreamName,
          source_id: item.sourceId,
          source_name: item.sourceName,
          source_description: item.sourceDescription,
          subscription_type: item.subscriptionType,
          primary_use_case: item.primaryUseCase,
          update_frequency: item.updateFrequency,
        });

      if (error) {
        // Handle duplicate key error specifically
        if (error.code === '23505') {
          toast.info('Dit item zit al in uw winkelmandje');
          // Reload cart to sync with database
          await loadCartFromDatabase();
          return;
        }
        throw error;
      }
      
      toast.success('Toegevoegd aan winkelmandje');
    } catch (error: any) {
      console.error('Error adding to cart:', error);
      
      // Check if it's a duplicate key error
      if (error?.code === '23505') {
        toast.info('Dit item zit al in uw winkelmandje');
        // Reload cart to sync with database
        await loadCartFromDatabase();
      } else {
        toast.error('Fout bij toevoegen aan winkelmandje');
      }
      
      // Revert optimistic update
      setItems(prev => prev.filter(i => i.datastreamId !== item.datastreamId));
    }
  };

  const removeFromCart = async (datastreamId: string) => {
    if (!user) return;

    // Optimistically update UI
    const previousItems = items;
    setItems(prev => prev.filter(i => i.datastreamId !== datastreamId));

    try {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('user_id', user.id)
        .eq('datastream_id', datastreamId);

      if (error) throw error;
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast.error('Fout bij verwijderen uit winkelmandje');
      // Revert optimistic update
      setItems(previousItems);
    }
  };

  const clearCart = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      setItems([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const getItemCount = () => items.length;

  const isInCart = (datastreamId: string) => {
    return items.some(i => i.datastreamId === datastreamId);
  };

  return (
    <CartContext.Provider value={{
      items,
      addToCart,
      removeFromCart,
      clearCart,
      getItemCount,
      isInCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
